import { openDatabase } from "../Utils/db";

import Studio2Database from "../screens/Dashboard/SubComponents/Studio/Adapters/Studio2Database";

import type { IStudio } from "../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import ScenesScreenshot from "./LivePreview/Strategy/ScenesScreenshot";

interface ISceneManagerOptions {
  models?: IStudio[] | null;
}

interface IWatchData {
  models: IStudio[];

  currentModel: IStudio | null;
}

export type IWatchTemplate = (data: IWatchData) => void;

class ScenesManager {
  private Models: Map<string, IStudio>;

  private CurrentModel: IStudio | null;

  private Events = new Map<string, Set<IWatchTemplate>>();

  constructor(options?: ISceneManagerOptions) {
    const currentModels =
      options?.models?.map((model) => [model.id, model] as const) ?? [];

    this.Models = new Map(currentModels);

    this.CurrentModel = options?.models?.[0] ?? null;
  }

  getModel() {
    return this.CurrentModel;
  }

  async getScene() {
    const currentModel = this.getModel();

    if (!currentModel) return null;

    const db = await openDatabase();

    const scenes = await db.scenes.toArray();

    const currentScene = scenes.find((scene) => scene.active);

    return currentScene;
  }

  getModels() {
    return Array.from(this.Models.values());
  }

  nextModel() {
    const models = this.getModels();

    const currentModel = this.getModel();

    const index = models.findIndex((model) => model?.id === currentModel?.id);

    const next = index + 1;

    if (!models[next]) return;

    this.setModel(models[next]);
  }

  /**
   * Aplica el modelo en memoria (mapa + actual + evento) SIN persistir. Lo usa el OSR window:
   * recibe el modelo que el control ya persistió, y volver a escribir la misma fila en Dexie
   * (leyendo antes TODAS las escenas) era trabajo muerto en cada edición al aire.
   */
  setModelInMemory(newModel: IStudio) {
    this.Models.set(newModel.id, newModel);

    this.CurrentModel = newModel;

    this.emit("model", {
      models: Array.from(this.Models.values()),

      currentModel: this.CurrentModel,
    });
  }

  async setModel(newModel: IStudio) {
    this.setModelInMemory(newModel);

    await this.updateModelDatabase();
  }

  private async updateModelDatabase() {
    const db = await openDatabase();

    const scenes = await db.scenes.toArray();

    for (const key in scenes) {
      const scene = scenes[key];

      if (scene.model.id === this.CurrentModel?.id && this.CurrentModel) {
        scenes[key].model = new Studio2Database(this.CurrentModel).format();

        await db.scenes

          .where("id")

          .equals(scene.id)

          .modify((item) => {
            if (this.CurrentModel) item.model = this.CurrentModel;
          });
      }
    }
  }

  async watch(type: string, callback: IWatchTemplate) {
    if (!this.Events.has(type)) {
      this.Events.set(type, new Set<IWatchTemplate>());
    }

    const callbacks = this.Events.get(type);

    callbacks?.add(callback);
  }

  async unwatch(type: string, callback: IWatchTemplate) {
    const callbacks = this.Events.get(type);

    callbacks?.delete(callback);

    if (callbacks && callbacks.size === 0) {
      this.Events.delete(type);
    }
  }

  private async emit(type: string, data: IWatchData) {
    const callbacks = this.Events.get(type);

    if (callbacks) {
      for (const callback of callbacks) {
        callback(data);
      }
    }
  }

  destroy() {
    this.Events.clear();

    this.Models.clear();

    this.CurrentModel = null;

    ScenesScreenshot.destroy();
  }
}

export default ScenesManager;
